<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import UserStatusLogsAPI from 'dashboard/api/userStatusLogs';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import Avatar from 'next/avatar/Avatar.vue';

const store = useStore();
const { t } = useI18n();

const agents = useMapGetter('agents/getAgents');
const globalConfig = useMapGetter('globalConfig/get');

const logs = ref([]);
const meta = ref({ total_count: 0, current_page: 1, total_pages: 1 });
const isLoading = ref(false);
const isDownloading = ref(false);
const isGeneratingPdf = ref(false);

const filters = ref({
  since: null,
  until: null,
  userId: null,
  status: null,
});

const currentPage = ref(1);

const STATUS_OPTIONS = [
  { value: null, label: t('USER_STATUS_REPORT.FILTERS.ALL_STATUSES') },
  { value: 'online', label: t('USER_STATUS_REPORT.STATUS.ONLINE') },
  { value: 'offline', label: t('USER_STATUS_REPORT.STATUS.OFFLINE') },
  { value: 'busy', label: t('USER_STATUS_REPORT.STATUS.BUSY') },
];

const STATUS_LABELS = {
  online: t('USER_STATUS_REPORT.STATUS.ONLINE'),
  offline: t('USER_STATUS_REPORT.STATUS.OFFLINE'),
  busy: t('USER_STATUS_REPORT.STATUS.BUSY'),
};

const STATUS_CLASSES = {
  online: 'bg-n-teal-3 text-n-teal-11',
  offline: 'bg-n-slate-3 text-n-slate-11',
  busy: 'bg-n-amber-3 text-n-amber-11',
};

const agentOptions = computed(() => [
  { id: null, name: t('USER_STATUS_REPORT.FILTERS.ALL_AGENTS') },
  ...agents.value,
]);

const fetchLogs = async (page = 1) => {
  isLoading.value = true;
  try {
    const { data } = await UserStatusLogsAPI.get({
      page,
      since: filters.value.since,
      until: filters.value.until,
      userId: filters.value.userId,
      status: filters.value.status,
    });
    logs.value = data.data;
    meta.value = data.meta;
    currentPage.value = page;
  } catch {
    useAlert(t('USER_STATUS_REPORT.ERRORS.LOAD_FAILED'));
  } finally {
    isLoading.value = false;
  }
};

const onDateRangeChange = ([start, end]) => {
  filters.value.since = start
    ? Math.floor(new Date(start).getTime() / 1000)
    : null;
  filters.value.until = end ? Math.floor(new Date(end).getTime() / 1000) : null;
  fetchLogs(1);
};

const onFilterChange = () => fetchLogs(1);

const downloadCSV = async () => {
  isDownloading.value = true;
  try {
    const { data } = await UserStatusLogsAPI.download({
      since: filters.value.since,
      until: filters.value.until,
      userId: filters.value.userId,
      status: filters.value.status,
    });
    downloadCsvFile('relatorio_status_usuarios.csv', data);
  } catch {
    useAlert(t('USER_STATUS_REPORT.ERRORS.DOWNLOAD_FAILED'));
  } finally {
    isDownloading.value = false;
  }
};

const formatDate = iso => {
  if (!iso) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'America/Sao_Paulo',
  }).format(new Date(iso));
};

const BADGE_STYLE = {
  online: 'background:#d1fae5;color:#065f46',
  offline: 'background:#f1f5f9;color:#475569',
  busy: 'background:#fef3c7;color:#92400e',
};

const CLOSE_SCRIPT = '\x3C/script>';

const fmtTs = ts =>
  ts
    ? new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'America/Sao_Paulo',
      }).format(new Date(ts * 1000))
    : null;

const printPDF = async () => {
  isGeneratingPdf.value = true;
  try {
    const logoPath = globalConfig.value?.logoThumbnail;
    let logoHtml = `<span style="font-size:20px;font-weight:700;color:#fff;letter-spacing:-0.5px">${globalConfig.value?.name || 'TalkChat'}</span>`;
    if (logoPath) {
      try {
        const res = await fetch(`${window.location.origin}${logoPath}`);
        const blob = await res.blob();
        const b64 = await new Promise(resolve => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        logoHtml = `<img src="${b64}" style="height:40px;max-width:160px;object-fit:contain;filter:brightness(0) invert(1)" alt="logo">`;
      } catch {
        /* fallback to text */
      }
    }

    const companyName = globalConfig.value?.name || 'TalkChat';

    const since = fmtTs(filters.value.since);
    const until = fmtTs(filters.value.until);
    let periodLabel;
    if (since && until) {
      periodLabel = `${since} até ${until}`;
    } else if (since) {
      periodLabel = `A partir de ${since}`;
    } else {
      periodLabel = 'Todos os períodos';
    }

    const selectedAgent = filters.value.userId
      ? agents.value.find(a => a.id === filters.value.userId)?.name ||
        'Desconhecido'
      : 'Todos os agentes';

    const selectedStatus = filters.value.status
      ? STATUS_LABELS[filters.value.status]
      : 'Todos';

    const now = new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Sao_Paulo',
    }).format(new Date());

    const badge = (status, label) =>
      `<span style="display:inline-block;padding:2px 10px;border-radius:20px;font-size:10px;font-weight:600;${BADGE_STYLE[status]}">${label}</span>`;

    const rows = logs.value
      .map(log => {
        const prev = log.previous_status
          ? badge(log.previous_status, STATUS_LABELS[log.previous_status])
          : '<span style="color:#94a3b8">—</span>';
        const curr = badge(
          log.current_status,
          STATUS_LABELS[log.current_status]
        );
        return `<tr>
          <td style="padding:9px 14px;border-bottom:1px solid #f1f5f9;color:#1e293b;font-weight:500">${log.user_name}</td>
          <td style="padding:9px 14px;border-bottom:1px solid #f1f5f9">${prev}</td>
          <td style="padding:9px 14px;border-bottom:1px solid #f1f5f9">${curr}</td>
          <td style="padding:9px 14px;border-bottom:1px solid #f1f5f9;color:#475569;font-variant-numeric:tabular-nums">${formatDate(log.created_at)}</td>
        </tr>`;
      })
      .join('');

    const emptyRow = `<tr><td colspan="4" style="text-align:center;padding:40px;color:#94a3b8;font-size:13px">Nenhum registro encontrado para os filtros selecionados.</td></tr>`;

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8">
<title>Relatório de Status de Usuários</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Arial,sans-serif;font-size:12px;color:#1e293b;background:#fff}
.header{background:#00B5A9;padding:20px 28px;display:flex;align-items:center;gap:16px}
.header-text h1{color:#fff;font-size:17px;font-weight:700}
.header-text p{color:rgba(255,255,255,.75);font-size:11px;margin-top:3px}
.meta{padding:14px 28px;background:#f8fafc;border-bottom:1px solid #e2e8f0;display:flex;gap:28px;flex-wrap:wrap}
.meta-item{font-size:11px;color:#64748b}
.meta-item strong{color:#1e293b;display:block;font-size:12px;margin-bottom:1px}
.content{padding:20px 28px}
.summary{font-size:11px;color:#64748b;margin-bottom:12px}
table{width:100%;border-collapse:collapse}
thead tr{background:#f8fafc;border-top:2px solid #00B5A9}
th{padding:10px 14px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#64748b}
tbody tr:last-child td{border-bottom:none}
.footer{margin:0 28px 20px;padding-top:12px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:10px;color:#94a3b8}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style>
</head>
<body>
<div class="header">
  ${logoHtml}
  <div class="header-text">
    <h1>Relatório de Status de Usuários</h1>
    <p>${companyName}</p>
  </div>
</div>
<div class="meta">
  <div class="meta-item"><strong>Período</strong>${periodLabel}</div>
  <div class="meta-item"><strong>Agente</strong>${selectedAgent}</div>
  <div class="meta-item"><strong>Status</strong>${selectedStatus}</div>
  <div class="meta-item"><strong>Registros</strong>${logs.value.length} (página atual)</div>
</div>
<div class="content">
  <table>
    <thead>
      <tr>
        <th>Usuário</th><th>Status Anterior</th><th>Status Atual</th><th>Data / Hora</th>
      </tr>
    </thead>
    <tbody>${rows || emptyRow}</tbody>
  </table>
</div>
<div class="footer">
  <span>Gerado em ${now}</span>
  <span>${companyName} · TalkChat</span>
</div>
<script>window.onload=function(){window.print()}${CLOSE_SCRIPT}
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    window.open(URL.createObjectURL(blob), '_blank');
  } finally {
    isGeneratingPdf.value = false;
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  fetchLogs();
});
</script>

<template>
  <div class="flex flex-col gap-6 print:gap-4">
    <div class="flex items-center justify-between print:hidden">
      <h2 class="text-xl font-semibold text-n-slate-12">
        {{ $t('USER_STATUS_REPORT.TITLE') }}
      </h2>
      <div class="flex gap-2">
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 transition-colors disabled:opacity-50"
          :disabled="isLoading"
          @click="fetchLogs(currentPage)"
        >
          <span
            :class="
              isLoading
                ? 'i-lucide-loader-2 animate-spin'
                : 'i-lucide-refresh-cw'
            "
            class="size-4"
          />
        </button>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 transition-colors disabled:opacity-50"
          :disabled="isDownloading"
          @click="downloadCSV"
        >
          <span class="i-lucide-file-spreadsheet size-4" />
          {{
            isDownloading
              ? $t('USER_STATUS_REPORT.EXPORTING')
              : $t('USER_STATUS_REPORT.EXPORT_CSV')
          }}
        </button>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 transition-colors disabled:opacity-50"
          :disabled="isGeneratingPdf"
          @click="printPDF"
        >
          <span
            :class="
              isGeneratingPdf
                ? 'i-lucide-loader-2 animate-spin'
                : 'i-lucide-file-text'
            "
            class="size-4"
          />
          {{
            isGeneratingPdf
              ? $t('USER_STATUS_REPORT.EXPORTING')
              : $t('USER_STATUS_REPORT.EXPORT_PDF')
          }}
        </button>
      </div>
    </div>

    <div class="flex flex-wrap gap-3 print:hidden">
      <WootDatePicker @date-range-changed="onDateRangeChange" />

      <select
        v-model="filters.userId"
        class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-teal-8"
        @change="onFilterChange"
      >
        <option v-for="agent in agentOptions" :key="agent.id" :value="agent.id">
          {{ agent.name }}
        </option>
      </select>

      <select
        v-model="filters.status"
        class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-teal-8"
        @change="onFilterChange"
      >
        <option
          v-for="opt in STATUS_OPTIONS"
          :key="String(opt.value)"
          :value="opt.value"
        >
          {{ opt.label }}
        </option>
      </select>
    </div>

    <div class="overflow-x-auto rounded-xl border border-n-weak">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-n-weak bg-n-surface-2">
            <th class="px-4 py-3 text-left font-medium text-n-slate-11">
              {{ $t('USER_STATUS_REPORT.TABLE.USER') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11">
              {{ $t('USER_STATUS_REPORT.TABLE.PREVIOUS_STATUS') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11">
              {{ $t('USER_STATUS_REPORT.TABLE.CURRENT_STATUS') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11">
              {{ $t('USER_STATUS_REPORT.TABLE.DATE_TIME') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading">
            <td colspan="4" class="px-4 py-8 text-center text-n-slate-11">
              <span
                class="i-lucide-loader-2 animate-spin inline-block size-5"
              />
            </td>
          </tr>
          <tr v-else-if="logs.length === 0">
            <td colspan="4" class="px-4 py-8 text-center text-n-slate-11">
              {{ $t('USER_STATUS_REPORT.NO_DATA') }}
            </td>
          </tr>
          <tr
            v-for="log in logs"
            :key="log.id"
            class="border-b border-n-weak last:border-0 hover:bg-n-alpha-1"
          >
            <td class="px-4 py-3">
              <div class="flex items-center gap-2">
                <Avatar
                  :name="log.user_name"
                  :src="log.user_avatar_url"
                  :size="24"
                  rounded-full
                />
                <span class="text-n-slate-12 font-medium">{{
                  log.user_name
                }}</span>
              </div>
            </td>
            <td class="px-4 py-3">
              <span
                v-if="log.previous_status"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                :class="STATUS_CLASSES[log.previous_status]"
              >
                {{ STATUS_LABELS[log.previous_status] }}
              </span>
              <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
              <span v-else class="text-n-slate-10">-</span>
            </td>
            <td class="px-4 py-3">
              <span
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                :class="STATUS_CLASSES[log.current_status]"
              >
                {{ STATUS_LABELS[log.current_status] }}
              </span>
            </td>
            <td class="px-4 py-3 text-n-slate-11 tabular-nums">
              {{ formatDate(log.created_at) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="meta.total_pages > 1"
      class="flex items-center justify-between print:hidden"
    >
      <span class="text-sm text-n-slate-11">
        {{ $t('USER_STATUS_REPORT.PAGINATION', { total: meta.total_count }) }}
      </span>
      <div class="flex gap-1">
        <button
          v-for="page in meta.total_pages"
          :key="page"
          class="px-3 py-1 text-sm rounded-lg border border-n-weak transition-colors"
          :class="
            page === currentPage
              ? 'bg-n-teal-9 text-white border-n-teal-9'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="fetchLogs(page)"
        >
          {{ page }}
        </button>
      </div>
    </div>
  </div>
</template>

<style>
@media print {
  body > *:not(#app) {
    display: none !important;
  }
  .sidebar,
  nav,
  header {
    display: none !important;
  }
}
</style>
